/**
 * Struktur menu dashboard — SATU sumber kebenaran untuk sidebar (10 Okt 2026).
 *
 * Kenapa spec, bukan tombol hard-code di App.tsx: dashboard OWNER (menu laporan +
 * langganan, menyusul) harus memakai sidebar yang SAMA. Dengan spec ini, item
 * menu diberi `roles`, dan dashboard owner cukup memanggil `navForRole('owner')`
 * — tidak ada daftar menu kedua yang bisa drift dari yang ini.
 *
 * Aturan yang dijaga `scripts/probe-dashboard-nav.ts`:
 *   - `key` unik dan SETIAP key punya panel yang dirender di App.tsx
 *     (`tab === '<key>'`) — item menu tanpa halaman = tombol mati;
 *   - role owner punya item (≥1) dan item itu memang berlaku untuk owner;
 *   - App.tsx membangun menu dari `navForRole(...)`, bukan tombol tulis tangan.
 *
 * Murni (tanpa impor React/DOM) supaya bisa dimuat probe Node
 * `--experimental-strip-types`.
 */

/** Peran pengguna dashboard. */
export type DashboardRole = "superadmin" | "owner";

export interface DashboardNavItem {
  /** Sama dengan key tab di App.tsx (`tab === key`). */
  key: string;
  icon: string;
  /** Label sidebar. */
  label: string;
  /** Kalimat penjelas (title attribute + header halaman). */
  hint: string;
  /** Role yang boleh melihat & membuka item ini. */
  roles: readonly DashboardRole[];
}

/**
 * Urutan di sini = urutan tampil di sidebar. Item owner-usable diletakkan dekat
 * atas supaya dashboard owner (nanti) tidak perlu mengurutkan ulang.
 */
export const DASHBOARD_NAV: readonly DashboardNavItem[] = [
  {
    key: "toko",
    icon: "🏪",
    label: "Toko",
    hint: "Nada bicara bot + identitas usaha (dipilih owner)",
    roles: ["superadmin", "owner"],
  },
  {
    key: "providers",
    icon: "📦",
    label: "Providers",
    hint: "Katalog provider & key AI Gateway",
    roles: ["superadmin"],
  },
  {
    key: "model",
    icon: "🔁",
    label: "Model",
    hint: "Urutan rotasi model chat & vision",
    roles: ["superadmin"],
  },
  {
    key: "llm",
    icon: "⚙️",
    label: "LLM",
    hint: "Parameter global: suhu obrolan, batas token, timeout",
    roles: ["superadmin"],
  },
  {
    key: "prompt",
    icon: "🧠",
    label: "Prompt",
    hint: "Prompt yang benar-benar dikirim ke LLM + riwayat versi",
    roles: ["superadmin"],
  },
  {
    key: "logs",
    icon: "📋",
    label: "Log",
    hint: "AI Gateway, debug, audit, arsip",
    roles: ["superadmin"],
  },
  {
    key: "stats",
    icon: "📈",
    label: "Usage",
    hint: "Pemakaian model & success rate",
    roles: ["superadmin"],
  },
  {
    key: "users",
    icon: "👤",
    label: "User",
    hint: "Semua user bot + soft delete",
    roles: ["superadmin"],
  },
];

/** Item menu yang berlaku untuk satu role (urutan sidebar dipertahankan). */
export function navForRole(role: DashboardRole): readonly DashboardNavItem[] {
  return DASHBOARD_NAV.filter((item) => item.roles.includes(role));
}

/** Item menu satu role; `null` bila key tidak berlaku untuk role itu. */
export function navItemFor(role: DashboardRole, key: string): DashboardNavItem | null {
  return navForRole(role).find((item) => item.key === key) ?? null;
}

/**
 * Tab pertama yang masuk akal untuk satu role (dipakai sebagai tab awal dan
 * fallback bila tab aktif tidak berlaku lagi untuk role itu).
 */
export function defaultNavKey(role: DashboardRole): string {
  return navForRole(role)[0]?.key ?? "";
}

/** Judul & subjudul sidebar per role (branding dashboard owner menyusul). */
export function dashboardBrand(role: DashboardRole): { title: string; subtitle: string } {
  return role === "owner"
    ? { title: "🏪 KasKita", subtitle: "Dashboard Pemilik Usaha" }
    : { title: "🔧 KasKita", subtitle: "Dashboard Superadmin" };
}
